import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { FileText, Plus, Loader2, ChevronRight, Search, Filter } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import EmptyState from "../components/EmptyState";
import { formatIndianDate, formatCurrencyINR } from "../utils/format";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  draft: { label: "Draft", bg: "bg-slate-100 text-slate-700" },
  sent: { label: "Sent", bg: "bg-blue-50 text-blue-700" },
  viewed: { label: "Viewed", bg: "bg-indigo-50 text-indigo-700" },
  pending_response: { label: "Pending Response", bg: "bg-amber-50 text-amber-700" },
  self_approved: { label: "Self Approved", bg: "bg-emerald-50 text-emerald-700" },
  client_approved_registered: { label: "Client Approved", bg: "bg-emerald-100 text-emerald-800" },
  client_approved_invited: { label: "Client Approved (Invited)", bg: "bg-emerald-50 text-emerald-700" },
  seller_marked_approved: { label: "Seller Marked Approved", bg: "bg-amber-100 text-amber-800" },
  rejected: { label: "Rejected", bg: "bg-red-50 text-red-700" },
  revision_requested: { label: "Revision Requested", bg: "bg-orange-50 text-orange-700" },
  converted_to_invoice: { label: "Converted", bg: "bg-violet-100 text-violet-800" },
  expired: { label: "Expired", bg: "bg-slate-100 text-slate-500" },
};

export default function QuotationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => { fetchList(); }, []);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quotations/list`, { withCredentials: true });
      setItems(res.data.quotations || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load quotations");
    } finally {
      setLoading(false);
    }
  };

  const filtered = items
    .filter(q => statusFilter === "all" || q.status === statusFilter)
    .filter(q => !search || (q.customer_name || "").toLowerCase().includes(search.toLowerCase()) || (q.quotation_number || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="min-h-screen bg-slate-50 py-6 sm:py-8 px-4" data-testid="quotations-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Quotations
            </h1>
            <p className="text-sm text-slate-500 mt-1">Send professional quotes, track client decisions, convert to invoice in one click.</p>
          </div>
          <Link to="/quotations/new">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-quotation-btn">
              <Plus className="w-4 h-4 mr-1" /> Create Quotation
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by client or quotation number..."
              className="pl-10"
              data-testid="quotation-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-56" data-testid="quotation-status-filter">
              <Filter className="w-4 h-4 mr-1 text-slate-400" />
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_STYLES).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={FileText}
            iconBg="bg-blue-50 text-blue-600"
            title={items.length === 0 ? "No quotations yet" : "No quotations match these filters"}
            description="Send a proposal to a client, let them approve from any device, then convert the approved quote to a GST invoice in one click."
            bullets={[
              "FY-based numbering (QT/2026-27/0001) with separate sequence from invoices",
              "Share via link — clients can accept/reject without creating an account",
              "Track Viewed → Approved → Converted status for every quote",
            ]}
            primaryAction={{ label: "Create your first quotation", to: "/quotations/new", icon: Plus }}
            testId="quotations-empty"
          />
        ) : (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="quotations-list">
            {filtered.map((q, idx) => {
              const st = STATUS_STYLES[q.status] || STATUS_STYLES.draft;
              return (
                <motion.div
                  key={q.quotation_id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.02 }}
                >
                  <Link
                    to={`/quotations/${q.quotation_id}`}
                    className="flex items-center gap-3 sm:gap-4 p-4 border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors"
                    data-testid={`quotation-row-${q.quotation_id}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="font-semibold text-slate-900 text-sm truncate">{q.customer_name}</span>
                        <span className="text-[11px] font-mono text-slate-400">{q.quotation_number}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500 mt-0.5">
                        <span>{formatIndianDate(q.quotation_date)}</span>
                        {q.valid_until && <span>Valid until {formatIndianDate(q.valid_until)}</span>}
                      </div>
                      <div className="flex sm:hidden items-center justify-between gap-2 mt-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.bg}`}>{st.label}</span>
                        <span className="text-sm font-bold text-slate-900">{formatCurrencyINR(q.grand_total)}</span>
                      </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${st.bg}`}>{st.label}</span>
                      <span className="text-base font-bold text-slate-900 min-w-[90px] text-right">{formatCurrencyINR(q.grand_total)}</span>
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
